import React, { useState, useEffect, useCallback } from 'react';
import { Text } from '@cuemath/web-comps';
import { usePatchAgentDetails, invalidateAgents } from 'actions/support-agent';
import {
  SENIOR_ROLES,
  ROLE_LABELS,
  AGENT_ACTIONS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
  removeAgentOpenTicketsTooltip,
  REMOVE_AGENT_LAST_LEAD_TOOLTIP,
} from '../../constants';
import {
  FormField,
  FormTextInput,
  TeamAddAgentList,
  TeamAddAgentItem,
  TeamAgentAvatar,
  TeamAgentInfo,
  TeamAgentName,
  TeamAgentEmail,
  TeamAgentMeta,
  TeamAgentTickets,
  AgentRoleBadge,
  AgentOnlineDot,
  TeamEmpty,
  ConfirmText,
  ConfirmBoldName,
  BtnDanger,
} from './styled';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnSecondary,
  IconBtn,
  ModalErrorMsg,
} from '../../styled-comps';
import Icon from '../icon';

const RemoveAgentModal = ({ open, agents, onClose }) => {
  const [query, setQuery] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const { patch: patchAgent, isProcessing: removing } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setSubmitError(typeof error === 'string' ? error : 'Failed to remove agent');
      } else {
        invalidateAgents(CACHE_KEYS.AGENTS);
        handleClose();
      }
    },
  });

  const handleClose = useCallback(() => {
    setQuery('');
    setConfirming(null);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!submitError) return undefined;
    const timer = setTimeout(() => setSubmitError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [submitError]);

  const handleConfirm = useCallback(() => {
    if (!confirming || removing) return;
    patchAgent(confirming.user_id, { action: AGENT_ACTIONS.DELETE });
  }, [confirming, removing, patchAgent]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const candidates = agents.filter(a => {
    if (!q) return true;
    return (
      (a.name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q)
    );
  });

  const seniors = agents.filter(a => SENIOR_ROLES.indexOf(a.role) !== -1);
  const lastSeniorId = seniors.length === 1 ? seniors[0].user_id : null;

  const guardFor = a => {
    if ((a.open_ticket_count || 0) > 0) {
      return removeAgentOpenTicketsTooltip(a.open_ticket_count);
    }
    if (a.user_id === lastSeniorId) {
      return REMOVE_AGENT_LAST_LEAD_TOOLTIP;
    }
    return null;
  };

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">
            {confirming ? 'Confirm Remove Agent' : 'Remove Agent'}
          </Text>
          <IconBtn onClick={handleClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>

        {confirming ? (
          <>
            <ModalBody>
              {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
              <ConfirmText>
                Remove <ConfirmBoldName>{confirming.name}</ConfirmBoldName> as a support
                agent? This cannot be undone.
              </ConfirmText>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirming(null)}>Back</BtnSecondary>
              <BtnDanger onClick={handleConfirm} disabled={removing}>
                {removing ? 'Removing...' : 'Remove Agent'}
              </BtnDanger>
            </ModalFoot>
          </>
        ) : (
          <ModalBody>
            {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
            <FormField>
              <FormTextInput
                autoFocus
                type="text"
                placeholder="Search agents by name or email..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </FormField>

            {candidates.length === 0 ? (
              <TeamEmpty>No matching agents</TeamEmpty>
            ) : (
              <TeamAddAgentList>
                {candidates.map(agent => {
                  const guard = guardFor(agent);
                  return (
                    <TeamAddAgentItem
                      key={agent.user_id}
                      disabled={!!guard}
                      title={guard || undefined}
                      onClick={() => !guard && setConfirming(agent)}
                    >
                      <TeamAgentAvatar>
                        {(agent.name || '--').slice(0, 2).toUpperCase()}
                        <AgentOnlineDot $online={agent.is_online} />
                      </TeamAgentAvatar>
                      <TeamAgentInfo>
                        <TeamAgentName>{agent.name}</TeamAgentName>
                        {agent.email && <TeamAgentEmail>{agent.email}</TeamAgentEmail>}
                        <TeamAgentMeta>
                          <AgentRoleBadge $role={agent.role}>
                            {ROLE_LABELS[agent.role] || agent.role}
                          </AgentRoleBadge>
                          {typeof agent.open_ticket_count === 'number' && (
                            <TeamAgentTickets>
                              {agent.open_ticket_count} open ticket
                              {agent.open_ticket_count === 1 ? '' : 's'}
                            </TeamAgentTickets>
                          )}
                        </TeamAgentMeta>
                      </TeamAgentInfo>
                    </TeamAddAgentItem>
                  );
                })}
              </TeamAddAgentList>
            )}
          </ModalBody>
        )}
      </ModalCard>
    </ModalOverlay>
  );
};

export default RemoveAgentModal;
